import { isTenantScoped, type AuthMeResponse } from "@shared/types/auth";

/**
 * Which building a session belongs in (`CONTENT-4`).
 *
 * `RequireAuth` checks only that somebody is logged in, and every page is
 * reachable by URL for any logged-in user — the nav merely hides entries. That
 * was tolerable while everybody with a login worked at Nenda or for a customer.
 * It is not tolerable with an external content owner in the building, so the
 * two audiences are now separated at the ROUTE, in both directions:
 *
 *  - a content owner must not reach `/fleet` by typing it;
 *  - an operator's stray link must not land them in a supplier's shell, where
 *    every surface is scoped to an owner they do not have and the page would
 *    render half-broken rather than refuse.
 *
 * The predicate is the session's owner scope and nothing else, deliberately.
 * Not the role (a role can be edited without the scope moving), not a
 * permission (a permission can be granted by mistake): the scope is the thing
 * the SERVER's `owner` tenancy stance reads, so the client and the server agree
 * about who somebody is by construction.
 */

/** True when this session speaks for a content owner. */
export function isContentOwnerSession(
  user: AuthMeResponse | null | undefined
): boolean {
  const owner = user?.contentOwner;
  return typeof owner === "string" && owner.trim() !== "";
}

/**
 * A customer's own staff: tied to a company or to granted locations, and not a
 * content owner. Nenda staff carry no tenant scope (an `admin` cannot - the
 * server reads a scoped admin as `companyAdmin`).
 */
export function isCustomerSession(
  user: AuthMeResponse | null | undefined
): boolean {
  return !!user && !isContentOwnerSession(user) && isTenantScoped(user);
}

/**
 * Where a customer starts: their Overview (Sigge, 2026-09-28/29). The
 * fleet-health dashboard is Nenda's estate view. `/overview` resolves to the
 * Frontlayer or Signage overview by the header's product selector - see
 * `nav/customerOverview.ts`.
 */
export const CUSTOMER_HOME = "/overview";

/** Where a freshly logged-in session belongs. */
export function landingPathFor(
  user: AuthMeResponse | null | undefined
): string {
  if (isContentOwnerSession(user)) return "/supplier/titles";
  if (isCustomerSession(user)) return CUSTOMER_HOME;
  const canViewFleet =
    user?.type === "admin" ||
    (user?.permissions ?? []).includes("canViewFleet");
  return canViewFleet ? "/analytics" : "/premises";
}
